import { Component, Input, Output, EventEmitter, ViewChild, OnInit } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { PurchasedLicensesInfo, PurchasedLicensesCrossRef } from '../data/purchased-licenses-info';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../auth.service';
import { OrganizationService } from '../organization.service';
import { RedirectService } from '../redirect.service';
import { Router } from '@angular/router';
import { DataService } from '../data/data.service';
import { Location } from '@angular/common';
import swal from 'sweetalert2/dist/sweetalert2.js';

@Component({
    selector: 'app-add-or-edit-licenses-modal',
    templateUrl: './add-or-edit-licenses-modal.component.html',
    styleUrls: ['./add-or-edit-licenses-modal.component.css']
})
export class AddOrEditLicensesModalComponent extends BaseComponent implements OnInit
{

    @Input() openModalButtonVisible: boolean = false;
    @Input() organizationDescription: string = '';
    @Input() organizationId: number;
    @Input() allLicenses: PurchasedLicensesInfo[] = [];
    @Output() licenseSaved = new EventEmitter<PurchasedLicensesInfo>();

    @ViewChild('modal') modal: any;

    license: PurchasedLicensesInfo = new PurchasedLicensesInfo();
    isEditMode: boolean = false;
    saving: boolean = false;
    previousStartDate: Date | null = null;

    // New properties for managing renewed purchases
    renewableLicenses: PurchasedLicensesInfo[] = []; // Filtered list for dropdown
    selectedRenewableLicenseId: number | null = null;

    constructor(
        protected route: ActivatedRoute,
        protected authService: AuthService,
        protected organizationService: OrganizationService,
        protected redirectService: RedirectService,
        protected router: Router,
        protected dataService: DataService,
        protected location: Location,
        private modalService: NgbModal
    )
    {
        super(route, authService, organizationService, redirectService, router, dataService, location);
    }

    ngOnInit(): void
    {
        // Initialize the PurchasesRenewedByThisPurchase array if it doesn't exist
        if (!this.license.PurchasesRenewedByThisPurchase)
        {
            this.license.PurchasesRenewedByThisPurchase = [];
        }
    }

    openForNew(): void
    {
        this.resetComponent();
        this.license.PurchasingOrganizationID = this.organizationId;
        this.license.PurchasingOrganizationDescription = this.organizationDescription;
        this.license.PurchasesRenewedByThisPurchase = [];
        this.isEditMode = false;
        this.updateRenewableLicenses();
        this.modalService.open(this.modal, { size: 'lg' });
    }

    private isOpeningModal = false;

    openForEdit(license: PurchasedLicensesInfo): void
    {
        // Prevent multiple calls
        if (this.isOpeningModal)
        {
            return;
        }

        this.isOpeningModal = true;
        this.resetComponent();
        this.license = { ...license };
        // Ensure the array exists
        if (!this.license.PurchasesRenewedByThisPurchase)
        {
            this.license.PurchasesRenewedByThisPurchase = [];
        }
        this.isEditMode = true;
        this.previousStartDate = license.LicenseStartDate ? this.parseDateAsLocal(license.LicenseStartDate) : null;
        this.updateRenewableLicenses();
        this.modalService.open(this.modal, { size: 'lg' });

        // Reset the flag after a short delay
        setTimeout(() =>
        {
            this.isOpeningModal = false;
        }, 1000);
    }

     save(): void
     {
         if (!this.validateForm())
         {
             return;
         }

         // Recalculate properties before saving since we're not refetching from backend
         this.recalculateLicenseProperties();

         this.saving = true;

         this.dataService.upsertPurchasedLicenses(this.license).subscribe(
            (savedLicense) =>
            {
                this.saving = false;

                // Check if the response is undefined, which indicates an error was handled by DataService
                if (!savedLicense)
                {
                    // Error was already shown by DataService.handleError, just close modal
                    this.modalService.dismissAll();
                    return;
                }

                this.modalService.dismissAll();
                this.licenseSaved.emit(this.license);
                swal.fire('Success!', 'License saved successfully.', 'success');
            },
            (error) =>
            {
                this.saving = false;
                console.error('Error saving license:', error);
                swal.fire('Error!', 'Failed to save license. Please try again.', 'error');
            }
        );
    }

    cancel(): void
    {
        this.modalService.dismissAll();
    }

    private validateForm(): boolean
    {
        if (!this.license.Quantity || this.license.Quantity <= 0)
        {
            this.showError('Quantity must be greater than 0');
            return false;
        }

        if (!this.license.PurchaseDate)
        {
            this.showError('Purchase Date is required');
            return false;
        }

        if (!this.license.LicenseStartDate)
        {
            this.showError('License Start Date is required');
            return false;
        }

        if (!this.license.LicenseEndDate)
        {
            this.showError('License End Date is required');
            return false;
        }

        if (!this.license.PurchaseDollarAmount || this.license.PurchaseDollarAmount <= 0)
        {
            this.showError('Purchase Amount must be greater than 0');
            return false;
        }

        if (!this.license.LicensePurchaseType)
        {
            this.showError('Purchase Type is required');
            return false;
        }

        if (!this.license.PurchaseLevel)
        {
            this.showError('Purchase Level is required');
            return false;
        }

        return true;
    }

    private showError(message: string): void
    {
        // TODO: Implement error display
        console.error(message);
    }

    calculateLicenseLength(): number
    {
        if (!this.license.LicenseStartDate || !this.license.LicenseEndDate)
        {
            return 0;
        }

        const startDate = this.parseDateAsLocal(this.license.LicenseStartDate);
        const endDate = this.parseDateAsLocal(this.license.LicenseEndDate);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);

        return Math.round(diffYears * 100) / 100; // Round to 2 decimal places
    }

    calculateARR(): number
    {
        if (!this.license.PurchaseDollarAmount || this.calculateLicenseLength() <= 0)
        {
            return 0;
        }

        return this.license.PurchaseDollarAmount / this.calculateLicenseLength();
    }

    calculateCostPerLicense(): number
    {
        if (this.calculateARR() <= 0 || !this.license.Quantity)
        {
            return 0;
        }

        return this.calculateARR() / this.license.Quantity;
    }

    // Format quantity with commas for display
    get formattedQuantity(): string
    {
        if (!this.license.Quantity) return '';
        return this.license.Quantity.toLocaleString();
    }

    set formattedQuantity(value: string)
    {
        // Remove commas and convert to number
        const numericValue = value.replace(/,/g, '');
        this.license.Quantity = numericValue ? parseInt(numericValue, 10) : null;
    }

    // Format purchase amount with commas for display
    get formattedPurchaseAmount(): string
    {
        if (!this.license.PurchaseDollarAmount) return '';
        return this.license.PurchaseDollarAmount.toLocaleString();
    }

    set formattedPurchaseAmount(value: string)
    {
        // Remove commas and convert to number
        const numericValue = value.replace(/,/g, '');
        this.license.PurchaseDollarAmount = numericValue ? parseFloat(numericValue) : null;
    }

    onLicenseStartDateChange(): void
    {
        if (this.license.LicenseStartDate)
        {
            // Parse as local date to avoid timezone issues
            const startDate = this.parseDateAsLocal(this.license.LicenseStartDate);

            // If there's already an end date and we have a previous start date, preserve the license length
            if (this.license.LicenseEndDate && this.previousStartDate)
            {
                const oldEndDate = this.parseDateAsLocal(this.license.LicenseEndDate);
                const licenseLengthMs = oldEndDate.getTime() - this.previousStartDate.getTime();

                const newEndDate = new Date(startDate);
                newEndDate.setTime(newEndDate.getTime() + licenseLengthMs);
                this.license.LicenseEndDate = newEndDate;
            } else if (this.license.LicenseEndDate)
            {
                // If there's an end date but no previous start date (first time setting start date)
                // Default to one year later
                const endDate = new Date(startDate);
                endDate.setFullYear(endDate.getFullYear() + 1);
                this.license.LicenseEndDate = endDate;
            } else
            {
                // If no end date exists, default to one year later
                const endDate = new Date(startDate);
                endDate.setFullYear(endDate.getFullYear() + 1);
                this.license.LicenseEndDate = endDate;
            }

            // Store the current start date as previous for next change
            this.previousStartDate = new Date(startDate);
        }
    }

    onPurchaseDateChange(): void
    {
        // Only auto-set dates if both license start and end dates are null
        if (!this.license.LicenseStartDate && !this.license.LicenseEndDate && this.license.PurchaseDate)
        {
            // Parse purchase date as local date
            const purchaseDate = this.parseDateAsLocal(this.license.PurchaseDate);

            // Set license start date to 1st of the month following the purchase date
            const licenseStartDate = new Date(purchaseDate);
            licenseStartDate.setMonth(licenseStartDate.getMonth() + 1);
            licenseStartDate.setDate(1);

            // Set license start date as Date object
            this.license.LicenseStartDate = licenseStartDate;

            // Set license end date to one year after the license start date
            const licenseEndDate = new Date(licenseStartDate);
            licenseEndDate.setFullYear(licenseEndDate.getFullYear() + 1);

            // Set license end date as Date object
            this.license.LicenseEndDate = licenseEndDate;

            // Store the current start date as previous for future changes
            this.previousStartDate = new Date(licenseStartDate);
        }
    }

    private parseDateAsLocal(dateString: string | Date): Date
    {
        if (typeof dateString === 'string')
        {
            // Parse date string as local date (YYYY-MM-DD format)
            const [year, month, day] = dateString.split('-').map(Number);
            return new Date(year, month - 1, day); // month is 0-indexed
        }
        return new Date(dateString);
    }

    isFormValid(): boolean
    {
        return !!(
            this.license.Quantity &&
            this.license.PurchaseDollarAmount &&
            this.license.PurchaseDate &&
            this.license.LicenseStartDate &&
            this.license.LicenseEndDate &&
            this.license.LicensePurchaseType &&
            this.license.PurchaseLevel
        );
    }

    private updateRenewableLicenses(): void
    {
        // Filter out the current license being edited to prevent self-reference
        if (this.isEditMode && this.license.PurchasedLicensesID)
        {
            this.renewableLicenses = this.allLicenses.filter(l => l.PurchasedLicensesID !== this.license.PurchasedLicensesID);
        } else
        {
            this.renewableLicenses = [...this.allLicenses];
        }
    }

    addRenewedPurchase(): void
    {
        if (!this.selectedRenewableLicenseId)
        {
            console.error('No license selected');
            return;
        }

        // Check if this license is already in the array
        const alreadyExists = this.license.PurchasesRenewedByThisPurchase.some(
            item => item.PurchasedLicensesID === this.selectedRenewableLicenseId
        );

        if (alreadyExists)
        {
            swal.fire('Warning', 'This license is already in the renewed purchases list.', 'warning');
            return;
        }

        // Add the license to the array
        const newCrossRef: PurchasedLicensesCrossRef = {
            PurchasedLicensesID: this.selectedRenewableLicenseId
        };
        this.license.PurchasesRenewedByThisPurchase.push(newCrossRef);

        // Reset selection
        this.selectedRenewableLicenseId = null;
    }

    removeRenewedPurchase(index: number): void
    {
        this.license.PurchasesRenewedByThisPurchase.splice(index, 1);
    }

    private formatLicenseDisplayInfo(license: PurchasedLicensesInfo): string
    {
        const purchaseDate = license.PurchaseDate ? new Date(license.PurchaseDate).toLocaleDateString() : 'N/A';
        const amount = license.PurchaseDollarAmount || 0;
        const orgName = license.PurchasingOrganizationDescription || 'N/A';
        return `${orgName} - ${purchaseDate} - $${amount.toLocaleString()}`;
    }

    getLicenseDisplayInfo(licenseId: number): string
    {
        // Search in the complete list of all licenses
        const license = this.allLicenses.find(l => l.PurchasedLicensesID == licenseId);
        if (license)
        {
            return this.formatLicenseDisplayInfo(license);
        }
        else
        {
            console.error("Couldn't find License Info for ID: " + licenseId);
        }

        // If not found, show just the ID
        return `License ID: ${licenseId}`;
    }

    getRenewableLicensesForDropdown(): PurchasedLicensesInfo[]
    {
        // Filter out licenses that are already in the renewed purchases list
        const selectedIds = this.license.PurchasesRenewedByThisPurchase.map(item => item.PurchasedLicensesID);
        return this.renewableLicenses.filter(license => !selectedIds.includes(license.PurchasedLicensesID));
    }

     private resetComponent(): void
     {
         this.license = new PurchasedLicensesInfo();
         this.isEditMode = false;
         this.saving = false;
         this.previousStartDate = null;
         this.selectedRenewableLicenseId = null;
         this.renewableLicenses = [];
     }

     private recalculateLicenseProperties(): void
     {
         // Calculate LicenseLengthYears
         if (this.license.LicenseStartDate && this.license.LicenseEndDate)
         {
             this.license.LicenseLengthYears = this.calculateLicenseLength();
         }

         // Calculate ARR (purchase amount divided by LicenseLengthYears)
         if (this.license.PurchaseDollarAmount && this.license.LicenseLengthYears && this.license.LicenseLengthYears > 0)
         {
             this.license.ARR = this.license.PurchaseDollarAmount / this.license.LicenseLengthYears;
         }
     }
}